export enum Currency {
  UAH = 0,
  USD = 1,
  EUR = 2,
  GBP = 3,
  PLN = 4,
  JPY = 5,
  CAD = 6,
  AUD = 7,
  CHF = 8,
  CNY = 9,
  RUB = 10,
  CZK = 11,
  DKK = 12,
  HUF = 13,
  RON = 14,
  SEK = 15,
  NOK = 16,
  TRY = 17,
  ILS = 18,
  AED = 19,
  SAR = 20,
  KZT = 21,
  BYN = 22
}

export interface Account {
  id: string;
  name: string;
  initialBalance: number;
  currentBalance: number;
  currency: Currency | string; // Може бути string з бекенду через JsonStringEnumConverter
  isDefault: boolean;
  isSavings?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccountRequest {
  name: string;
  initialBalance: number;
  currency?: Currency;
  isSavings?: boolean;
}

export interface UpdateAccountRequest {
  name: string;
  currency?: Currency;
  isDefault?: boolean;
  isSavings?: boolean; // Якщо не передано — бекенд залишає поточне значення
}

export interface Category {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  transactionsCount?: number;
  archivedTransactionsCount?: number;
}

export interface CreateCategoryRequest {
  name: string;
}

export interface Receipt {
  id: string;
  name: string;
  accountId: string;
  accountName?: string;
  account?: Account;
  totalAmount: number;
  status: number; 
  type: number; 
  positions: Position[];
  date: string;
  isDeleted?: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptListItem {
  id: string;
  name: string;
  accountId: string;
  accountName?: string;
  totalAmount: number;
  status: number; 
  type: number; 
  positionsCount: number;
  date: string;
  isDeleted?: boolean;
}

export interface CreateReceiptRequest {
  name: string;
  accountId: string;
  type?: number;
  date?: string;
}

export interface CreateReceiptWithPositionsRequest {
  name: string;
  accountId: string;
  type?: number;
  date?: string;
  positions: Array<{
    name: string;
    amount: number;
    isBought: boolean;
    categoryId: string;
  }>;
}

export interface UpdateReceiptRequest {
  name?: string;
  accountId?: string;
  type?: number;
  date?: string;
}

export interface Position {
  id: string;
  name: string;
  amount: number;
  isBought: boolean;
  categoryId?: string;
  categoryName?: string;
  category?: Category;
  receiptId: string;
  createdAt: string;
  updatedAt: string;
  createdDate?: string;
  isDeleted?: boolean;
}

export interface CreatePositionRequest {
  name: string;
  amount: number;
  isBought?: boolean;
  categoryId?: string;
}

export interface UpdatePositionRequest {
  name?: string;
  amount?: number;
  isBought?: boolean;
  categoryId?: string;
}

export interface PositionSuggestionResponse {
  name: string;
  categoryId?: string;
  categoryName?: string;
  count: number;
}

export interface Budget {
  id: string;
  name: string;
  budgetType: string; 
  startDate: string;
  endDate: string;
  totalPlannedAmount: number;
  totalActualAmount: number;
  isActive: boolean;
  budgetCategories: BudgetCategory[];
  createdAt: string;
  updatedAt: string;
}

export interface BudgetCategory {
  id: string;
  budgetId: string;
  categoryId: string;
  categoryName: string;
  plannedAmount: number;
  actualAmount: number;
  remainingAmount: number;
  progressPercentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBudgetRequest {
  name: string;
  budgetType: string;
  startDate: string;
  endDate: string;
  totalPlannedAmount: number;
  budgetCategories: {
    categoryId: string;
    plannedAmount: number;
  }[];
}

export interface UpdateBudgetRequest {
  name?: string;
  budgetType?: string;
  startDate?: string;
  endDate?: string;
  totalPlannedAmount?: number;
  budgetCategories?: {
    categoryId: string;
    plannedAmount: number;
  }[];
}

export interface RecurringExpense {
  id: string;
  name: string;
  amount: number;
  categoryId: string;
  categoryName: string;
  accountId: string;
  accountName: string;
  recurrenceType: string; 
  dayOfMonth?: number;
  dayOfWeek?: number;
  nextDueDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRecurringExpenseRequest {
  name: string;
  amount: number;
  categoryId: string;
  accountId: string;
  recurrenceType: string; 
  dayOfMonth?: number;
  dayOfWeek?: number;
}

export interface UpdateRecurringExpenseRequest {
  name?: string;
  amount?: number;
  categoryId?: string;
  accountId?: string;
  recurrenceType?: string; 
  dayOfMonth?: number;
  dayOfWeek?: number;
  isActive?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
  message?: string;
  errors?: string[];
  refreshToken?: string;
  expiresAt?: string;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  createdAt: string;
  emailVerified?: boolean;
  isActive?: boolean;
  preferences?: Record<string, any>;
  updatedAt?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
}

export interface ShoppingListCategory {
  name: string;
  apiCategoryId?: string;
  items: ShoppingListItem[];
}

export interface ShoppingListItem {
  id: string;
  name: string;
  checked: boolean;
  price: number;
  positionId?: string;
  createdDate?: string;
}

export interface CurrentList {
  name: string;
  status: 'planning' | 'shopping';
  categories: Record<string, ShoppingListCategory>;
  accountId?: string;
  receiptId?: string;
}

export enum TransactionType {
  Expense = 0,
  Income = 1
}

export enum ReceiptStatus {
  InProgress = 0,
  Completed = 1
}

export enum TransferType {
  Regular = 'Regular',
  Savings = 'Savings'
}

export interface Transfer {
  id: string;
  fromAccountId: string;
  fromAccountName: string;
  toAccountId: string;
  toAccountName: string;
  fromAmount: number;
  toAmount?: number;
  exchangeRate?: number;
  date: string;
  description: string;
  transferType: TransferType;
}

export interface CreateTransferRequest {
  fromAccountId: string;
  toAccountId: string;
  fromAmount: number;
  toAmount?: number;
  exchangeRate?: number;
  description: string;
  transferType: TransferType;
  date?: string;
}

export interface GetTransfersFilters {
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  transferType?: TransferType;
}

export interface UpdateTransferRequest {
  fromAccountId: string;
  toAccountId: string;
  fromAmount: number;
  toAmount?: number;
  exchangeRate?: number;
  description: string;
  transferType: TransferType;
  date?: string;
}

// Loan types
export enum LoanType {
  ILent = 0,      // Я позичив комусь
  IBorrowed = 1   // Мені позичили
}

export enum LoanStatus {
  Active = 0,
  PartiallyRepaid = 1,
  Repaid = 2
}

export interface Loan {
  id: string;
  contactName: string;
  amount: number;
  repaidAmount: number;
  accountId?: string;
  accountName?: string;
  loanType: LoanType | string; // Backend returns string "ILent" or "IBorrowed"
  status: LoanStatus | string; // Backend returns string "Active", "PartiallyRepaid", or "Repaid"
  date: string;
  dueDate?: string;
  description?: string;
  payments?: LoanPayment[]; // Payments included in loan response
  createdAt: string;
  updatedAt: string;
}

export interface CreateLoanRequest {
  contactName: string;
  amount: number;
  accountId?: string;
  loanType: LoanType;
  date?: string;
  dueDate?: string;
  description?: string;
}

export interface UpdateLoanRequest {
  contactName: string;
  amount: number;
  repaidAmount: number;
  accountId?: string;
  loanType: LoanType;
  status: LoanStatus;
  date?: string;
  dueDate?: string;
  description?: string;
}

export interface GetLoansRequest {
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  loanType?: LoanType;
  status?: LoanStatus;
}

export interface LoanPayment {
  id: string;
  loanId: string;
  amount: number;
  paymentDate: string;
  description?: string;
}

export interface CreateLoanPaymentRequest {
  loanId: string;
  amount: number;
  paymentDate?: string;
  description?: string;
}

export interface UpdateLoanPaymentRequest {
  amount: number;
  paymentDate?: string;
  description?: string;
}

export interface CategoryStatisticsResponse {
  categories: CategoryStatistic[];
  totalAmount: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface CategoryStatistic {
  categoryId: string;
  categoryName: string;
  amount: number;
  transactionsCount: number;
  percentage: number;
}

export interface StatisticsFilters {
  period: 'week' | 'month' | 'year' | 'custom';
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  selectedMonth?: number; // 0-11 (0 = січень)
  selectedYear?: number;
  selectedWeek?: string; // YYYY-WW формату
}

export interface AvailableDatesResponse {
  dates: string[];
}

export interface CategoryTransactionsResponse {
  categoryId: string;
  categoryName: string;
  transactions: TransactionInfo[];
  totalAmount: number;
  totalCount: number;
}

export interface TransactionInfo {
  positionId: string;
  receiptId: string;
  receiptName: string;
  positionName: string;
  accountName: string;
  amount: number;
  date: string;
}

// Monthly plan types
export interface MonthlyPlanIncome {
  id: string;
  name: string;
  amount: number;
}

export interface MonthlyPlanFixedExpense {
  id: string;
  name: string;
  amount: number;
  paymentsCount: number;
  dayOfMonth?: number | null;
  recurringExpenseId?: string | null;
  recurrenceType?: string | null;
  categoryId?: string | null;
  categoryName?: string | null; // Для пунктів із шаблону — категорія шаблону
  accountName?: string | null;
}

export interface MonthlyPlanSavingsGoal {
  id: string;
  accountId: string;
  accountName: string;
  currency: Currency | string;
  amount: number;
}

export interface MonthlyPlan {
  id: string;
  year: number;
  month: number; // 1-12
  totalIncome: number;
  totalFixedExpenses: number;
  totalSavings: number;
  freeAmount: number;
  createdAt: string;
  updatedAt: string;
  incomes: MonthlyPlanIncome[];
  fixedExpenses: MonthlyPlanFixedExpense[];
  savingsGoals: MonthlyPlanSavingsGoal[];
}

export interface MonthlyPlanTemplatePayment {
  receiptId: string;
  receiptName: string;
  recurringExpenseId: string;
  accountId: string;
  isSavingsAccount: boolean;
  date: string;
  amount: number;
}

export interface MonthlyPlanExpense {
  date: string;
  categoryId: string;
  amount: number;
}

export interface MonthlyPlanSavingsTransfer {
  transferId: string;
  accountId: string; // Рахунок заощаджень
  date: string;
  amount: number; // Поповнення > 0, зняття < 0
}

export interface MonthlyPlanActivity {
  dateFrom: string;
  dateTo: string;
  totalIncome: number;
  templatePayments: MonthlyPlanTemplatePayment[];
  expenses: MonthlyPlanExpense[];
  savingsTransfers: MonthlyPlanSavingsTransfer[];
}

export interface MonthlyPlanOverview {
  year: number;
  month: number;
  plan: MonthlyPlan | null;
  activity: MonthlyPlanActivity;
}

export interface MonthlyPlanTemplateIncome {
  name: string;
  amount: number;
}

export interface MonthlyPlanTemplateFixedExpense {
  name: string;
  amount: number;
  paymentsCount: number;
  dayOfMonth?: number | null;
  recurringExpenseId?: string | null;
  recurrenceType?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  accountName?: string | null;
  isSelected: boolean;
}

export interface MonthlyPlanTemplateSavingsGoal {
  accountId: string;
  accountName: string;
  currency: Currency | string;
  currentBalance: number;
  amount: number;
  isSelected: boolean;
}

export interface MonthlyPlanTemplate {
  year: number;
  month: number;
  sourcePlanYear?: number | null;
  sourcePlanMonth?: number | null;
  incomes: MonthlyPlanTemplateIncome[];
  fixedExpenses: MonthlyPlanTemplateFixedExpense[];
  savingsGoals: MonthlyPlanTemplateSavingsGoal[];
}

export interface MonthlyPlanIncomeRequest {
  name: string;
  amount: number;
}

export interface MonthlyPlanFixedExpenseRequest {
  name: string;
  amount: number;
  paymentsCount: number;
  dayOfMonth?: number | null;
  recurringExpenseId?: string | null;
  categoryId?: string | null;
}

export interface MonthlyPlanSavingsGoalRequest {
  accountId: string;
  amount: number;
}

export interface CreateMonthlyPlanRequest {
  year: number;
  month: number;
  incomes: MonthlyPlanIncomeRequest[];
  fixedExpenses: MonthlyPlanFixedExpenseRequest[];
  savingsGoals: MonthlyPlanSavingsGoalRequest[];
}

export interface UpdateMonthlyPlanRequest {
  incomes: MonthlyPlanIncomeRequest[];
  fixedExpenses: MonthlyPlanFixedExpenseRequest[];
  savingsGoals: MonthlyPlanSavingsGoalRequest[];
}
